/**
 * What a visitor came for, read off the `?referrer=` a marketing landing page puts on its CTAs.
 *
 * `symphony.co/lp-en/shop` is the Shopify landing page; every CTA on it opens the app as
 * `symphony.wix.com/?referrer=https%3A%2F%2Fwww.symphony.co%2Flp-en%2Fshop`. The harness turns that intent
 * into Shopify-first onboarding (behind `specs.symphony.ShopifyLandingOnboarding`), so the intent has to
 * survive the trip from the first document request to the onboarding kickoff — through the sign-in bounce
 * and the `/welcome` redirect, neither of which keeps the page alive. Hence `localStorage`, with a short
 * TTL: an intent is about THIS visit, not a standing preference.
 *
 * Pure decision functions first, with no DOM, so they can be unit-tested; the thin storage wrapper is at
 * the bottom. Same shape as `referral-capture.ts`.
 */

export type LandingIntent = 'shopify';

/** Product-named, not the legacy `orion-web:` prefix (see the note in orion-web/CLAUDE.md). */
export const LANDING_INTENT_STORAGE_KEY = 'symphony-landing-intent';

/** Long enough to sign up and come back later today or tomorrow; short enough that a shared machine forgets. */
export const LANDING_INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** The landing site's host, with or without `www.`. */
export const LANDING_HOST = /^(?:www\.)?symphony\.co$/i;

/** The Shopify landing page path, in any locale variant (`/lp-en/shop`, `/lp/shop`, `/lp-fr/shop`). */
const SHOPIFY_LANDING_PATH = /^\/lp(?:-[a-z]{2,3})?\/shop\/?$/i;

export interface StoredLandingIntent {
  intent: LandingIntent;
  /** Who this was captured for. '' when the caller could not be identified at capture time. */
  userId: string;
  capturedAt: number;
}

/**
 * The intent a `referrer` value names, or null when it is not a landing page we recognise. Strict on
 * purpose: the value is public and client-supplied, and it ends up in a persisted conversation row and a
 * prompt, so only an exact known page may produce an intent.
 */
export function landingIntentFromReferrer(raw: unknown): LandingIntent | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!LANDING_HOST.test(url.hostname)) return null;
  if (SHOPIFY_LANDING_PATH.test(url.pathname)) return 'shopify';
  return null;
}

/**
 * The intent to spend now, or null. Rejects an expired record, and one captured for a different signed-in
 * user, so whoever uses this browser next does not inherit someone else's landing page. A record with no
 * userId stays spendable: the caller could not be identified at capture, the same rule referral capture uses.
 */
export function landingIntentToSpend({
  stored,
  userId,
  now,
}: {
  stored: StoredLandingIntent | null;
  userId: string;
  now: number;
}): LandingIntent | null {
  if (!stored) return null;
  if (stored.intent !== 'shopify') return null;
  if (now - stored.capturedAt > LANDING_INTENT_TTL_MS) return null;
  if (stored.userId && userId && stored.userId !== userId) return null;
  return stored.intent;
}

/** Parses whatever is in storage, tolerating anything a previous version or a user may have left. */
export function parseStoredLandingIntent(rawJson: string | null): StoredLandingIntent | null {
  if (!rawJson) return null;
  try {
    const value = JSON.parse(rawJson) as Partial<StoredLandingIntent>;
    if (value?.intent !== 'shopify' || typeof value.capturedAt !== 'number') return null;
    return { intent: 'shopify', userId: typeof value.userId === 'string' ? value.userId : '', capturedAt: value.capturedAt };
  } catch {
    return null;
  }
}

// ---- storage wrapper. Every path is guarded: Safari private mode throws on write, and a landing intent
// is never worth breaking a signup over.

export function saveLandingIntent(record: StoredLandingIntent): void {
  try {
    window.localStorage.setItem(LANDING_INTENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // No storage (private mode, quota, disabled) — the intent is simply not carried.
  }
}

export function loadLandingIntent(userId: string): LandingIntent | null {
  try {
    return landingIntentToSpend({
      stored: parseStoredLandingIntent(window.localStorage.getItem(LANDING_INTENT_STORAGE_KEY)),
      userId,
      now: Date.now(),
    });
  } catch {
    return null;
  }
}

export function clearLandingIntent(): void {
  try {
    window.localStorage.removeItem(LANDING_INTENT_STORAGE_KEY);
  } catch {
    // Nothing to do — see saveLandingIntent.
  }
}
